import { useState } from "react";
import { motion } from "framer-motion";
import {
  BarChart3,
  LineChart,
  ShieldCheck,
  Sparkles,
  Mail,
  CheckCircle,
  ArrowRight,
  PieChart,
} from "lucide-react";
import "react-phone-number-input/style.css";
import { API_BASE_URL } from "@/lib/apiInterceptor";

const APPLICATION_NAME = "Finsight";
const ACCENT_COLOR = "hsl(210 80% 45%)";

const ContactForm = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
    phoneNumber: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (errors[e.target.name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[e.target.name];
        return next;
      });
    }
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Name is required";
    if (!form.email.trim()) {
      next.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      next.email = "Enter a valid email";
    }
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setSubmitError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim(),
          ...(form.company ? { company: form.company.trim() || undefined } : {}),
          ...(form.phoneNumber ? { phoneNumber: form.phoneNumber || undefined } : {}),
          ...(form.message ? { message: form.message.trim() || undefined } : {}),
          applicationName: APPLICATION_NAME,
        }),
      });
      if (!response.ok) {
        throw new Error(`Request failed (${response.status})`);
      }
      setSubmitted(true);
      setForm({
        name: "",
        email: "",
        company: "",
        message: "",
        phoneNumber: "",
      });
    } catch (err) {
      setSubmitError(
        err instanceof Error
          ? err.message
          : "Something went wrong. Please try again.",
      );
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <motion.div
        className="flex flex-col items-center text-center py-12 gap-4"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
      >
        <div
          className="w-16 h-16 rounded-full flex items-center justify-center shadow-lg"
          style={{ background: ACCENT_COLOR }}
        >
          <CheckCircle className="w-8 h-8 text-white" />
        </div>
        <h3 className="text-2xl font-bold text-gray-900">Request Submitted!</h3>
        <p className="text-gray-500 max-w-sm">
          Thanks for reaching out. Our team will get back to you within one
          business day.
        </p>
        <button
          onClick={() => setSubmitted(false)}
          className="btn-primary mt-2 px-6 py-2.5"
        >
          Send another Request
        </button>
      </motion.div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4" noValidate>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
            Full Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={form.name}
            onChange={handleChange}
            placeholder="Jane Smith"
            className="input-field"
          />
          {errors.name && (
            <p className="text-xs text-red-500 mt-1">{errors.name}</p>
          )}
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
            Company
          </label>
          <input
            type="text"
            name="company"
            value={form.company}
            onChange={handleChange}
            placeholder="Acme Corp"
            className="input-field"
          />
        </div>
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
          Work Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          name="email"
          value={form.email}
          onChange={handleChange}
          placeholder="jane@company.com"
          className="input-field"
        />
        {errors.email && (
          <p className="text-xs text-red-500 mt-1">{errors.email}</p>
        )}
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
          Contact Number
        </label>
        <input
          type="tel"
          name="phoneNumber"
          value={form.phoneNumber}
          onChange={handleChange}
          placeholder="123-456-7890"
          className="input-field"
        />
      </div>

      <div>
        <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wide">
          Message
        </label>
        <textarea
          name="message"
          rows={4}
          value={form.message}
          onChange={handleChange}
          placeholder="Tell us about your financial analytics needs..."
          className="input-field resize-none"
        />
      </div>

      {submitError && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
          {submitError}
        </div>
      )}

      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full py-3 text-base disabled:opacity-70"
      >
        {loading ? (
          <span className="flex items-center justify-center gap-2">
            <svg
              className="animate-spin h-4 w-4 text-white"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8v8z"
              />
            </svg>
            Submitting
          </span>
        ) : (
          <>Submit</>
        )}
      </button>
    </form>
  );
};

const Finsight = () => {
  const scrollToContact = () => {
    document
      .getElementById("contact-section")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const features = [
    {
      icon: <BarChart3 className="w-8 h-8" />,
      title: "Financial Analytics",
      description:
        "AI-powered analysis of financial data, trends, and performance metrics",
      bgColor: "bg-blue-600",
    },
    {
      icon: <LineChart className="w-8 h-8" />,
      title: "Real-time Insights",
      description:
        "Monitor market movements and portfolio performance with live dashboards",
      bgColor: "bg-indigo-600",
    },
    {
      icon: <PieChart className="w-8 h-8" />,
      title: "Portfolio Intelligence",
      description:
        "Understand allocation, exposure, and opportunities across your holdings",
      bgColor: "bg-teal-600",
    },
    {
      icon: <ShieldCheck className="w-8 h-8" />,
      title: "Risk Assessment",
      description:
        "Identify risks early with predictive models and compliance-aware alerts",
      bgColor: "bg-emerald-600",
    },
  ];

  const steps = [
    {
      step: "1",
      title: "Connect Your Data",
      description:
        "Import financial statements, market feeds, or portfolio data securely",
    },
    {
      step: "2",
      title: "Configure Dashboards",
      description:
        "Set up KPIs, watchlists, and alerts tailored to your investment strategy",
    },
    {
      step: "3",
      title: "Generate Insights",
      description:
        "AI agents analyze patterns and surface actionable financial intelligence",
    },
    {
      step: "4",
      title: "Act with Confidence",
      description:
        "Export reports, share findings with stakeholders, and track outcomes",
    },
  ];

  return (
    <div className="w-full min-h-screen">
      <div className="w-full px-6 lg:px-12 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 max-w-4xl mx-auto"
        >
          <h1 className="text-5xl md:text-6xl font-bold mb-6 text-black leading-tight">
            Finsight Intelligence Platform
          </h1>
          <p className="text-xl text-gray-600 mb-8 max-w-2xl mx-auto">
            Combines financial data, market intelligence, and AI-driven analytics
            to deliver portfolio insights, risk signals, and decision support
            for modern finance teams
          </p>
          <div className="flex justify-center">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={scrollToContact}
              className="btn-simulation text-lg px-8 py-4"
              style={{ backgroundColor: ACCENT_COLOR }}
            >
              <Mail className="w-5 h-5" />
              Contact Us
              <ArrowRight className="w-5 h-5" />
            </motion.button>
          </div>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16 max-w-7xl mx-auto">
          {features.map((feature, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="card hover:shadow-2xl transition-all duration-300 group"
            >
              <div
                className={`w-16 h-16 rounded-2xl p-4 text-white mb-4 group-hover:scale-110 transition-transform shadow-lg ${feature.bgColor}`}
              >
                {feature.icon}
              </div>
              <h3
                className="text-xl font-bold mb-2"
                style={{ color: ACCENT_COLOR }}
              >
                {feature.title}
              </h3>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </div>

        <div className="max-w-7xl mx-auto flex flex-row justify-center mb-16">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative w-full max-w-4xl overflow-hidden rounded-sm min-h-[320px] flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${ACCENT_COLOR} 0%, hsl(200 70% 35%) 100%)`,
            }}
          >
            <div className="text-center px-8 py-16 text-white">
              <Sparkles className="w-12 h-12 mx-auto mb-4 opacity-90" />
              <h3 className="text-2xl font-bold mb-2">Smarter Financial Decisions</h3>
              <p className="text-white/90 max-w-lg mx-auto">
                Finsight helps finance teams move from raw data to clear,
                actionable intelligence — faster.
              </p>
            </div>
            <div className="absolute top-4 left-4 px-3 py-1 rounded-full bg-white/90 backdrop-blur text-xs font-semibold text-gray-800 shadow">
              Product Preview
            </div>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="card max-w-4xl mx-auto"
        >
          <h2 className="text-3xl font-bold text-center mb-8">
            <span className="text-black">How It </span>
            <span style={{ color: ACCENT_COLOR }}>Works</span>
          </h2>
          <div className="space-y-6">
            {steps.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.5, delay: 0.5 + index * 0.1 }}
                className="flex items-start gap-4 p-4 rounded-xl hover:bg-gray-50 transition-colors"
              >
                <div
                  className="flex-shrink-0 w-12 h-12 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg"
                  style={{ backgroundColor: ACCENT_COLOR }}
                >
                  {item.step}
                </div>
                <div>
                  <h3
                    className="text-lg font-semibold mb-1"
                    style={{ color: ACCENT_COLOR }}
                  >
                    {item.title}
                  </h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        <motion.div
          id="contact-section"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="max-w-7xl mx-auto mt-24 scroll-mt-24"
        >
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl font-bold text-black mb-3">
              Ready to transform your{" "}
              <span style={{ color: ACCENT_COLOR }}>financial workflows?</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Talk to our team to see how Finsight can help your organization
              make faster, data-driven financial decisions.
            </p>
          </div>

          <div className="flex flex-row justify-center gap-8 items-stretch">
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="card w-[800px] max-w-full"
            >
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-gray-900 mb-1">
                  Request
                </h3>
                <p className="text-gray-500 text-sm">
                  Tell us about your financial analytics needs and we&apos;ll
                  reach out shortly.
                </p>
              </div>
              <ContactForm />
            </motion.div>
          </div>
        </motion.div>
      </div>

      <footer className="w-full py-6 px-6 border-t border-gray-100 text-center text-sm text-gray-500 mt-8">
        © {new Date().getFullYear()} Finsight Intelligence Platform. All rights
        reserved.
      </footer>
    </div>
  );
};

export default Finsight;
